import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, NavItem, Row, TabContent, TabPane, PopoverBody } from 'reactstrap';
import { Browser } from '@syncfusion/ej2-base';

import { ReactComponent as SettingsIcon } from 'assets/icon-settings.svg';
import { ReactComponent as MenuIcon } from 'assets/icon-kebab-menu.svg';
import BackgroundBox from 'shared/components/BackgroundBox';
import { LabelIcon, LabelInput } from 'shared/components/Labels';
import { ButtonIcon } from 'shared/components/Buttons';
import { PopoverSort } from 'shared/components/SharedStyles/SharedStyles';

import { colors } from 'shared/defaultTheme';
import { addBreadcrumb, removeBreadcrumb } from 'redux/actions/layoutActions';

import CustomFolder from './components/CustomFolder/CustomFolder';
import CustomField from './components/CustomField/CustomField';
import Tags from './components/Tags/Tags';
import ClientStates from './components/States/ClientStates';
import DocumentStates from './components/States/DocumentStates';
import { SettingsWrapper, NavHome, NavLink, OptionMenu } from './styles';

const Settings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { breadcrumbs } = useSelector((state) => state.layout);
  const theme = useSelector((state) => state.theme);
  const [activeTab, setActiveTab] = useState('1');
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { mainColor } = theme;

  useEffect(() => {
    setActiveTab(activeTab);
  }, [selectedDefaultLanguage]);

  useEffect(() => {
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
      dispatch(
        addBreadcrumb({
          active: false,
          label: 'general_home',
          link: '/home',
          isModify: false,
        })
      );
    }

    dispatch(
      addBreadcrumb({
        label: 'general_settings',
        active: true,
        link: pathname,
        isModify: true,
      })
    );
    dispatch(
      addBreadcrumb({
        label: 'Settings_custom_folder',
        active: true,
        link: null,
        isModify: false,
      })
    );
  }, []);

  const getActiveTab = (numberTab, title) => {
    setActiveTab(numberTab);
    dispatch(
      addBreadcrumb({
        label: title,
        active: true,
        link: null,
        isModify: false,
      })
    );
  };

  return (
    <SettingsWrapper>
      <BackgroundBox className="backgroundbox-wrapper">
        <div className="d-flex justify-content-between align-items-center">
          <LabelIcon
            label={t('general_settings')}
            Icon={() => <SettingsIcon className="label-icon__all" fill={colors.white} />}
            color={mainColor}
            className="text-uppercase ml-1 label-icon"
          />
          {Browser.isDevice && (
            <>
              <ButtonIcon
                id="menuActions"
                type="button"
                Icon={() => <MenuIcon fill={colors.black} height={20} width={15} />}
                className="button-icon"
              />
              <PopoverSort trigger="legacy" placement="bottom" target="menuActions">
                <PopoverBody>
                  <OptionMenu
                    onClick={() => getActiveTab('1', 'Settings_custom_folder')}
                    aria-hidden="true"
                  >
                    <LabelInput
                      label={t('Settings_custom_folder')}
                      className="ml-1 body-text--primary action-option-order-label"
                    />
                  </OptionMenu>
                  <OptionMenu
                    onClick={() => getActiveTab('2', 'Settings_custom_fields')}
                    aria-hidden="true"
                  >
                    <LabelInput
                      label={t('Settings_custom_fields')}
                      className="ml-1 body-text--primary action-option-order-label"
                    />
                  </OptionMenu>
                  <OptionMenu
                    onClick={() => getActiveTab('4', 'Settings_document_status')}
                    aria-hidden="true"
                  >
                    <LabelInput
                      label={t('Settings_document_status')}
                      className="ml-1 body-text--primary action-option-order-label"
                    />
                  </OptionMenu>
                  <OptionMenu
                    onClick={() => getActiveTab('6', 'Settings_client_status')}
                    aria-hidden="true"
                  >
                    <LabelInput
                      label={t('Settings_client_status')}
                      className="ml-1 body-text--primary action-option-order-label"
                    />
                  </OptionMenu>

                  <OptionMenu
                    onClick={() => getActiveTab('5', 'Settings_labels')}
                    aria-hidden="true"
                  >
                    <LabelInput
                      label={t('Settings_labels')}
                      className="ml-1 body-text--primary action-option-order-label"
                    />
                  </OptionMenu>
                </PopoverBody>
              </PopoverSort>
            </>
          )}
        </div>

        <NavHome tabs>
          <NavItem>
            <NavLink
              color={activeTab === '1' ? mainColor : colors.pattens}
              selected={activeTab === '1'}
              onClick={() => getActiveTab('1', 'Settings_custom_folder')}
            >
              {t('Settings_custom_folder')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              color={activeTab === '2' ? mainColor : colors.pattens}
              selected={activeTab === '2'}
              onClick={() => getActiveTab('2', 'Settings_custom_fields')}
            >
              {t('Settings_custom_fields')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              color={activeTab === '4' ? mainColor : colors.pattens}
              selected={activeTab === '4'}
              onClick={() => getActiveTab('4', 'Settings_document_status')}
            >
              {t('Settings_document_status')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              color={activeTab === '6' ? mainColor : colors.pattens}
              selected={activeTab === '6'}
              onClick={() => getActiveTab('6', 'Settings_client_status')}
            >
              {t('Settings_client_status')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              color={activeTab === '5' ? mainColor : colors.pattens}
              selected={activeTab === '5'}
              onClick={() => getActiveTab('5', 'Settings_labels')}
            >
              {t('Settings_labels')}
            </NavLink>
          </NavItem>
        </NavHome>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">{activeTab === '1' && <CustomFolder />}</TabPane>
          <TabPane tabId="2">
            {activeTab === '2' && (
              <Row>
                <Col sm="12">
                  <CustomField />
                </Col>
              </Row>
            )}
          </TabPane>
          <TabPane tabId="3">
            <Row>
              <Col sm="12">
                <div />
              </Col>
            </Row>
          </TabPane>
          <TabPane tabId="4">
            {activeTab === '4' && (
              <Row>
                <Col sm="12">
                  <DocumentStates />
                </Col>
              </Row>
            )}
          </TabPane>
          <TabPane tabId="6">
            {activeTab === '6' && (
              <Row>
                <Col sm="12">
                  <ClientStates />
                </Col>
              </Row>
            )}
          </TabPane>
          <TabPane tabId="5">
            {activeTab === '5' && (
              <Row>
                <Col sm="12">
                  <Tags />
                </Col>
              </Row>
            )}
          </TabPane>
        </TabContent>
      </BackgroundBox>
    </SettingsWrapper>
  );
};

export default Settings;
